import { UNLABELLED_ASSET_LABEL, INDEX_FALLBACK } from "@overmap-ai/common"
import type { AssetTypeIdentifier, AssetTypeStatus, Stored } from "@overmap-ai/common"
import { FieldSchema } from "@overmap-ai/forms"
import * as turf from "@turf/turf"

import type { AssetData } from "../../typings"
import { combineColumnDefs, CSVExporter } from "./CSVExporter"
import type { CSVColumnDef } from "./typings"
import { getCSVColumnDefsOfFieldSchema } from "./utils/fields"
import { getCSVColumnDefsOfIdentifiers } from "./utils/identifiers"

interface AssetDataCsvExporterConfig {
	assetTypeFieldSchema: FieldSchema
	assetTypeStatuses: Stored<AssetTypeStatus>[]
	assetTypeIdentifiers: Stored<AssetTypeIdentifier>[]
}

export const getAssetDataCsvExporter = (data: AssetData[], config: AssetDataCsvExporterConfig) => {
	const { assetTypeFieldSchema, assetTypeStatuses, assetTypeIdentifiers } = config

	const assetTypeStatusesById = Object.fromEntries(
		assetTypeStatuses.map((assetTypeStatus) => [assetTypeStatus.uuid, assetTypeStatus]),
	)

	const baseColumns: CSVColumnDef<AssetData>[] = [
		{ id: "uuid", header: "uuid", accessorFn: (row) => row.asset.uuid },
		{ id: "index", header: "Index", accessorFn: (row) => row.asset.index ?? INDEX_FALLBACK },
		{ id: "label", header: "Label", accessorFn: (row) => row.asset.label || UNLABELLED_ASSET_LABEL },
		{ id: "geometry", header: "Geometry", accessorFn: (row) => JSON.stringify(row.asset.geometry) },
		{
			id: "lat_centroid",
			header: "Lat centroid",
			accessorFn: (row) => {
				if (!row.asset.geometry) return ""
				const centroid = turf.centroid(row.asset.geometry)
				return centroid.geometry.coordinates[1].toString()
			},
		},
		{
			id: "long_centroid",
			header: "Long centroid",
			accessorFn: (row) => {
				if (!row.asset.geometry) return ""
				const centroid = turf.centroid(row.asset.geometry)
				return centroid.geometry.coordinates[0].toString()
			},
		},
		{
			id: "status",
			header: "Status",
			accessorFn: (row) => {
				const assetTypeStatus = assetTypeStatusesById[row.asset.status]
				return assetTypeStatus.name
			},
		},
	]

	const assetTypeIdentifierColumns = getCSVColumnDefsOfIdentifiers(
		assetTypeIdentifiers,
		(identifier) => (row: AssetData) => row.assetTypeIdentifierValues[identifier.uuid]?.value,
	)

	const assetTypeFieldColumns = getCSVColumnDefsOfFieldSchema<AssetData>(
		assetTypeFieldSchema,
		(row) => row.assetTypeFieldValues?.values,
	)

	return new CSVExporter({
		columns: combineColumnDefs(baseColumns, assetTypeIdentifierColumns, assetTypeFieldColumns),
		data: data,
	})
}
